import { QUESTION_TYPE } from '../components/Activity/Question';
import type {
  CmsProjectDefinition,
  CmsProjectQuestion,
} from '../schemas/applied-project';
import type { ActivityPayload, QuestionPayload } from '../hydra';

/**
 * Maps a CMS question (camelCase) to the API question payload (snake_case)
 * expected by ActivityProvider / ActivityLayout.
 */
function cmsQuestionToPayload(
  q: CmsProjectQuestion,
  index: number
): QuestionPayload {
  const now = new Date().toISOString();

  return {
    id: -(index + 1),
    updated_at: now,
    created_at: now,
    type: q.type as QUESTION_TYPE,
    text: q.text,
    hints: q.hints ?? null,
    exercise_id: null,
    slug: q.slug,
    setup_sql: 'setupSQL' in q ? (q.setupSQL as string) : null,
    answer_dql:
      'answerDQL' in q ? (q.answerDQL as string | null) ?? null : null,
    verification_sql:
      'verificationSQL' in q
        ? (q.verificationSQL as string | null) ?? null
        : null,
    index_in_exercise: q.indexInExercise,
    looker_link:
      'lookerLink' in q ? (q.lookerLink as string | null) ?? null : null,
    tableau_link:
      'tableauLink' in q ? (q.tableauLink as string | null) ?? null : null,
    google_sheets_link:
      'googleSheetsLink' in q
        ? (q.googleSheetsLink as string | null) ?? null
        : null,
    model_answer: q.modelAnswer ?? null,
    notes: q.notes ?? null,
    powerbi_csv: 'powerbiCSV' in q ? (q.powerbiCSV as string) : null,
    initial_py:
      'initialPy' in q ? (q.initialPy as string | null) ?? null : null,
    output_regexes_py:
      'outputRegexesPy' in q
        ? (q.outputRegexesPy as string[] | null) ?? null
        : null,
    input_regexes_py:
      'inputRegexesPy' in q
        ? (q.inputRegexesPy as string[] | null) ?? null
        : null,
    expect_error_py:
      'expectErrorPy' in q ? (q.expectErrorPy as boolean | null) ?? null : null,
    intro_lesson_text: q.introLessonText,
    setup_py: 'setupPy' in q ? (q.setupPy as string | null) ?? null : null,
    llm_initial_prompt:
      'llmInitialPrompt' in q
        ? (q.llmInitialPrompt as string | null) ?? null
        : null,
    multiple_choice_answers:
      'multipleChoiceAnswers' in q && q.multipleChoiceAnswers
        ? {
            correct_answer: (
              q.multipleChoiceAnswers as {
                correctAnswer: string;
                distractors: string[];
              }
            ).correctAnswer,
            distractors: (
              q.multipleChoiceAnswers as {
                correctAnswer: string;
                distractors: string[];
              }
            ).distractors,
          }
        : null,
    strict_text_answers:
      'strictTextAnswers' in q ? (q.strictTextAnswers as string[]) : null,
  };
}

/**
 * Converts a CmsProjectDefinition into a mock ActivityPayload suitable for
 * rendering in the learner-facing ActivityLayout. All IDs are synthetic
 * negatives so they never collide with real DB rows.
 */
export function cmsProjectToActivityResponse(
  def: CmsProjectDefinition
): ActivityPayload {
  const now = new Date().toISOString();

  return {
    id: -1,
    updated_at: now,
    created_at: now,
    is_core: null,
    title: def.name,
    summary: def.summary,
    external_url: null,
    type: 'EXERCISE',
    mission_id: null,
    estimate_seconds: 3600,
    index_in_mission: 0,
    exercise_id: null,
    is_teams_only: false,
    slug: def.slug,
    activity_session_id: -1,
    activity_session_instructor_review: null,
    activity_session_instructor_score: null,
    activity_session_instructor_question_comments: [],
    latest_submission: null,
    exercise: {
      id: -1,
      updated_at: now,
      created_at: now,
      description: def.description,
      lesson: def.lesson,
      difficulty: 2,
      activity_id: -1,
      slug: def.slug,
      questions: def.questions.map((q, i) => cmsQuestionToPayload(q, i)),
    },
    content_id: -1,
    content_slug: def.topic,
    content_title: def.topic,
    next_activity_slug: null,
    next_mission_slug: null,
  };
}
